/**
 * Hold a card in the ranked ten and this comes up: share, or delete.
 *
 * Two actions, because the grid is ranked by slot and promoting or demoting
 * belongs to the grid itself rather than to a menu. Delete frees the slot -
 * which is the only place a slot is ever freed, here or inside the celebration,
 * never on its own.
 */

import { useState } from 'react';
import { Share2, Trash2, X } from 'lucide-react';
import BottomSheet from '../BottomSheet';
import { useTheme } from '../../context/ThemeContext';
import haptics from '../../utils/haptics';
import { shareCard } from '../../services/wishShare';

export default function WishCardHoldSheet({ isOpen, card, position, onClose, onDelete }) {
  const { theme } = useTheme();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');

  const close = () => { if (!busy) { setError(''); onClose?.(); } };

  const doShare = async () => {
    setBusy('share');
    setError('');
    try {
      const res = await shareCard({
        setId: card?.set_id,
        cardId: card?.tcgdex_card_id,
        name: card?.card_name,
      });
      if (res.method !== 'clipboard') { onClose?.(); return; }
      setError('Link copied.');
    } catch (e) {
      console.warn('[wish card] share failed', e?.message);
      setError('That share did not go through.');
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async () => {
    setBusy('delete');
    setError('');
    try {
      haptics.warn();
      await onDelete?.(card);
      onClose?.();
    } catch (e) {
      console.warn('[wish card] delete failed', e?.message);
      setError('Could not remove that card. Try again.');
      setBusy(null);
    }
  };

  return (
    <BottomSheet
      open={!!isOpen && !!card}
      onClose={close}
      label="Chase card actions"
      overlayStyle={{ ...styles.overlay, backgroundColor: theme.overlay }}
      sheetStyle={{ ...styles.sheet, backgroundColor: theme.bgSheet }}
    >
      <div style={styles.handle} />
      <div style={styles.header}>
        <div style={styles.titleBlock}>
          <h2 style={{ ...styles.title, color: theme.text }}>{card?.card_name || 'Chase card'}</h2>
          <span style={{ ...styles.meta, color: theme.textMuted }}>
            {position ? `Slot ${position}` : ''}{position && card?.set_name ? ' · ' : ''}{card?.set_name || ''}
          </span>
        </div>
        <button
          style={{ ...styles.closeBtn, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.textSecondary }}
          onClick={close}
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>

      {error && <p style={{ ...styles.error, color: theme.warning }}>{error}</p>}

      <button
        style={{ ...styles.row, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.text }}
        onClick={doShare}
        disabled={busy === 'share'}
      >
        <Share2 size={18} color={theme.accent} />
        <span style={styles.rowLabel}>Share this card</span>
      </button>
      <button
        style={{ ...styles.row, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.error }}
        onClick={doDelete}
        disabled={busy === 'delete'}
      >
        <Trash2 size={18} color={theme.error} />
        <span style={styles.rowLabel}>{busy === 'delete' ? 'Removing...' : 'Remove and free the slot'}</span>
      </button>
    </BottomSheet>
  );
}

const styles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10002 },
  sheet: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    borderRadius: '20px 20px 0 0',
    padding: '12px 20px calc(24px + var(--safe-area-bottom, 0px))',
    zIndex: 10003,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
  },
  handle: { width: '36px', height: '4px', backgroundColor: '#3a3a3a', borderRadius: '2px', margin: '0 auto 12px' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '6px' },
  titleBlock: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 },
  title: { fontSize: '17px', fontWeight: '600', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  meta: { fontSize: '12px' },
  closeBtn: {
    width: '30px', height: '30px', borderRadius: '8px', border: '1px solid', display: 'flex',
    alignItems: 'center', justifyContent: 'center', cursor: 'pointer', outline: 'none', flexShrink: 0,
    WebkitTapHighlightColor: 'transparent',
  },
  error: { fontSize: '13px', margin: '0 0 4px', fontWeight: '600' },
  row: {
    display: 'flex', alignItems: 'center', gap: '12px', minHeight: '52px', padding: '0 14px',
    borderRadius: '12px', border: '1px solid', cursor: 'pointer', textAlign: 'left',
    outline: 'none', WebkitTapHighlightColor: 'transparent',
  },
  rowLabel: { fontSize: '15px', fontWeight: '600' },
};
